import { FC } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PunchupTile, TileIcon } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { toSigDigits } from 'utils/utils';
import { useHighlights } from 'features/common/hooks/useHighlights';
import { useGetScanHistory } from 'hooks/useGetScanHistory';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useEventCapacity } from 'features/common/hooks/useEventCapacity';
import { REVENUE_HELP_COPY } from 'features/common/utils/revenueHelpCopy';
import { useCompedInventory } from '../useCompedInventory';
import { useIsSeatedEvent } from '../analyticsContext';
import { computeAvgLeadTimeMinutes, computeRevenueCompRate, formatLeadTime, formatPct } from './utils';
import styles from './PunchupShowPerformanceSection.module.css';

const PunchupShowPerformanceSection: FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const { push } = useHistory();
	// Each table-backed KPI tile redirects to the sub-page that breaks its number down row by row —
	// the same redirect pattern the dashboard highlight tiles use. Lead Time has no backing table, so
	// it stays a static (informational) tile.
	const goToSubPage = (route: string) => () => push(generatePath(route, { id }));
	const { eventHighlights, isLoading } = useHighlights(id);
	// Shares the React Query cache with PunchupAnalytics — no extra network request.
	const { event } = useGetEvent();
	// Per-type comped counts (shared analytics hook) feed the revenue-weighted Comp
	// Rate here and the Ticket Inventory comped donut — same React Query cache key.
	const compedByType = useCompedInventory(id);
	// Shared pool-aware capacity — single source of truth for the Fill Rate denominator
	// AND the per-type comped/price data behind the revenue-weighted Comp Rate, so this
	// KPI and the Ticket Inventory grid can never drift apart.
	const { totals: capacityTotals, types: capacityTypes } = useEventCapacity(compedByType);
	// Seated events have no shared-capacity pools, so the Fill Rate help drops the pool wording. Read from
	// the single page-wide check (set once at PunchupAnalytics) rather than re-deriving it here.
	const isSeated = useIsSeatedEvent();
	// Show-up Rate uses /tickets/history (shared cache with the Show Day arrivals chart): totalScanned is
	// UNIQUE validated tickets and totalSold is issued (paid + comped), so the ratio is consistent on both
	// sides and ≤100%. Highlights.nums_of_scans is NOT used — it counts scan EVENTS (re-entry/comp/pass
	// rescans), which over-counts and can exceed tickets_sold, rendering >100%.
	const { data: scanHistory } = useGetScanHistory();

	if (isLoading)
		return (
			<div className={styles.loadingShell}>
				<PunchupSpinner size={32} />
			</div>
		);
	if (!eventHighlights) return null;

	const { total_payout, sales } = eventHighlights;

	// Denominator from the shared useEventCapacity source: effectiveCapacity is POOL-CAPPED — a pooled tier's
	// contribution is bounded by the shared pool (a pool smaller than its members' combined amount lowers the
	// real ceiling), so the Fill Rate, the Show-Day attendance donut, and the Ticket Inventory summary center
	// all reconcile. Parity-gate fallback: the raw event ticket-type sum is used only before the shared source
	// loads (effectiveCapacity === 0). amount = sellable seats after holds; holdsAmount = held seats.
	const eventCapacity = event?.ticketTypes?.reduce((sum, tt) => sum + tt.amount + tt.holdsAmount, 0) ?? 0;
	const totalCapacity = capacityTotals.effectiveCapacity > 0 ? capacityTotals.effectiveCapacity : eventCapacity;

	// Comp Rate is revenue-weighted: the share of issued-ticket VALUE given away as
	// comps (Σ comped×price ÷ Σ issued×price), so comping a VIP weighs more than a GA.
	// Conversion Rate is not used here — its GA4 page-view denominator is structurally
	// blank in the iframe/staging and >100% live. See CLAUDE.md.
	const compRateRatio = computeRevenueCompRate(capacityTypes);
	const compRate = compRateRatio !== null ? formatPct(compRateRatio) : '—';
	// Numerator is ISSUED (sold + comped, via the shared source), NOT the paid-only Highlights tickets_sold —
	// a comped seat fills the house. Same denominator (pool-capped effectiveCapacity) as the attendance donut
	// and the inventory summary center, so all three reconcile. A legacy over-subscribed pool can read >100%
	// (honest — the new-event flow tightening prevents it being created; docs/pools_business_logic.md §4).
	const capacityPct = totalCapacity > 0 ? formatPct(capacityTotals.issued / totalCapacity) : '—';
	// Unique scanned ÷ issued (both from /tickets/history), clamped — a ticket can't be scanned without
	// being issued, so the clamp only guards backend edge cases (e.g. re-scan races).
	const scannedTickets = scanHistory?.totalScanned ?? 0;
	const issuedTickets = scanHistory?.totalSold ?? 0;
	const showUpRate = issuedTickets > 0 ? formatPct(Math.min(1, scannedTickets / issuedTickets)) : '—';

	const leadTimeMinutes = computeAvgLeadTimeMinutes(sales, event?.startDate);
	const avgLeadTime = leadTimeMinutes !== null ? `~${formatLeadTime(leadTimeMinutes)}` : '—';

	return (
		<div className={styles.kpiGrid}>
			<PunchupTile
				title={t('analytics-kpi-lead-time')}
				topRight={<TileIcon iconName="calendarWhite" />}
				value={avgLeadTime}
				descriptions={[t('analytics-kpi-lead-time-desc')]}
				helpButton={
					<PunchupHelpButton
						mainText="How far in advance of the show the average ticket was purchased. A long lead time means buyers are committing early — the on-sale launch and promotion are driving sustained demand."
						codeText={
							'weighted avg of (days before show × tickets sold that day) ÷ total tickets\n~ = approximate; purchases are tracked day by day, not minute by minute'
						}
					/>
				}
			/>
			<PunchupTile
				title={t('analytics-kpi-utilization')}
				onClick={goToSubPage(ROUTES.ATTENDEES)}
				topRight={<TileIcon iconName="layout" />}
				value={capacityPct}
				descriptions={[t('analytics-kpi-utilization-desc')]}
				helpButton={
					isSeated ? (
						<PunchupHelpButton
							mainText="The percentage of total venue capacity filled by issued tickets. Per-type holds count as capacity, not open inventory."
							codeText={
								'fill rate = issued (sold + comped) ÷ capacity\ncapacity = Σ (amount + holds) per ticket type\nexample: 500-seat venue, 100 on hold, 350 issued → 90% full'
							}
							secondaryText="Issued counts comped seats too — a comped seat still fills the house."
						/>
					) : (
						<PunchupHelpButton
							mainText="The percentage of total venue capacity filled by issued tickets. Per-type holds count as capacity (not open inventory); shared pools cap it — a pool smaller than its tiers' combined size limits how many can ever sell."
							codeText={
								'fill rate = issued (sold + comped) ÷ effective capacity\neffective capacity = standalone (amount + holds) + each pool min(pool size, Σ member amount) + member holds\nexample: 500-seat venue, 100 on hold, 350 issued → 90% full'
							}
							secondaryText="Pooled tiers share one bucket, so their combined capacity is the pool size, not the sum of their own quantities. Issued counts comped seats too."
						/>
					)
				}
			/>
			<PunchupTile
				title={t('analytics-kpi-comp-rate')}
				onClick={goToSubPage(ROUTES.COMPED_TICKETS)}
				topRight={<TileIcon iconName="mailWhite" />}
				value={compRate}
				descriptions={[t('analytics-kpi-comp-rate-desc')]}
				helpButton={
					<PunchupHelpButton
						mainText="The share of would-be ticket revenue given away as comps rather than sold — comped tickets valued at their ticket price, against the value of all issued tickets (paid + comped). Weighting by price means a comped VIP counts for more than a comped GA."
						codeText={'comp rate = comped ticket value ÷ (paid + comped ticket value)'}
						secondaryText="Sits beside Fill Rate: how full the house is, and how much of that house went out free."
					/>
				}
			/>
			<PunchupTile
				title={t('analytics-kpi-showup')}
				/* No onClick — this KPI is a rate, not a row count, so it reads as an informational tile like Lead
				   Time. (The Scanned Tickets log itself IS reachable — from the dashboard Scanned tile, PUN-4942
				   req. 5a — but drilling from a percentage into a per-ticket log is a different question.)
				   See ./CLAUDE.md. */
				topRight={<TileIcon iconName="userCheckWhite" />}
				value={showUpRate}
				descriptions={[t('analytics-kpi-showup-desc')]}
				helpButton={
					<PunchupHelpButton
						mainText="The percentage of ticket holders who actually attended — unique tickets scanned at the door ÷ all tickets issued (paid + comped). Only meaningful once scanning begins."
						codeText={
							'show-up rate = unique tickets scanned ÷ tickets issued\nre-entry / multi-scan does not inflate it — each ticket counts once'
						}
					/>
				}
			/>
			<PunchupTile
				title={t('analytics-kpi-revenue')}
				onClick={goToSubPage(ROUTES.ORDERS_BY_EVENT_PATH)}
				topRight={<TileIcon iconName="dollarSignWhite" />}
				value={toSigDigits(total_payout)}
				descriptions={[t('analytics-kpi-revenue-desc')]}
				helpButton={
					/* Same figure as the Show Dashboard "Revenue" tile → identical copy (single source). */
					<PunchupHelpButton
						mainText={REVENUE_HELP_COPY.mainText}
						codeText={REVENUE_HELP_COPY.codeText}
						secondaryText={REVENUE_HELP_COPY.secondaryText}
					/>
				}
			/>
		</div>
	);
};

export default PunchupShowPerformanceSection;
